import { ref, computed } from 'vue';
import client from '../api/client.js';
import i18n from '../i18n.js';

const { t } = i18n.global;

export function useDashboardMetrics() {
  // ── State ──────────────────────────────────────────────────────────────────
  const adminMetrics = ref(null);
  const marketplaceMetrics = ref(null);
  const groupMetrics = ref(null);
  const selectedMetricType = ref('GLOBAL');
  const marketplaceFilterValue = ref('');

  const adminLoading = ref(false);
  const marketplaceLoading = ref(false);
  const groupLoading = ref(false);

  const adminError = ref(null);
  const marketplaceError = ref(null);
  const groupError = ref(null);

  // ── Computed ───────────────────────────────────────────────────────────────
  const lastAdminUpdate = computed(() =>
    adminMetrics.value?.metricDate
      ? t('dashboard.lastUpdated') + ' ' + adminMetrics.value.metricDate
      : '',
  );
  const lastMarketplaceUpdate = computed(() =>
    marketplaceMetrics.value?.metricDate
      ? t('dashboard.lastUpdated') + ' ' + marketplaceMetrics.value.metricDate
      : '',
  );
  const lastGroupUpdate = computed(() =>
    groupMetrics.value?.metricDate
      ? t('dashboard.lastUpdated') + ' ' + groupMetrics.value.metricDate
      : '',
  );

  // ── Admin Dashboard Fetchers ────────────────────────────────────────────────
  async function fetchAdminCurrent() {
    adminLoading.value = true;
    adminError.value = null;
    try {
      const { data } = await client.get('/admin/dashboard/current');
      adminMetrics.value = data;
      return data;
    } catch (err) {
      adminError.value = err.response?.data?.message || err.message;
      throw err;
    } finally {
      adminLoading.value = false;
    }
  }

  async function fetchAdminForDate(date) {
    adminLoading.value = true;
    adminError.value = null;
    try {
      const { data } = await client.get('/admin/dashboard/date/' + date);
      adminMetrics.value = data;
      return data;
    } catch (err) {
      adminError.value = err.response?.data?.message || err.message;
      throw err;
    } finally {
      adminLoading.value = false;
    }
  }

  async function fetchAdminHistorical(startDate, endDate) {
    adminLoading.value = true;
    adminError.value = null;
    try {
      const { data } = await client.get('/admin/dashboard/historical', {
        params: { startDate, endDate },
      });
      return data;
    } catch (err) {
      adminError.value = err.response?.data?.message || err.message;
      throw err;
    } finally {
      adminLoading.value = false;
    }
  }

  async function refreshAdminDaily() {
    try {
      await client.post('/admin/dashboard/refresh/daily');
      return fetchAdminCurrent();
    } catch (err) {
      adminError.value = err.response?.data?.message || err.message;
      throw err;
    }
  }

  // ── Marketplace Dashboard Fetchers ──────────────────────────────────────────
  async function fetchMarketplaceCurrent() {
    marketplaceLoading.value = true;
    marketplaceError.value = null;
    try {
      const { data } = await client.get('/marketplace/dashboard/current');
      marketplaceMetrics.value = data;
      return data;
    } catch (err) {
      marketplaceError.value = err.response?.data?.message || err.message;
      throw err;
    } finally {
      marketplaceLoading.value = false;
    }
  }

  async function fetchMarketplaceByType(metricType, filterValue = '') {
    marketplaceLoading.value = true;
    marketplaceError.value = null;
    selectedMetricType.value = metricType;
    marketplaceFilterValue.value = filterValue;
    try {
      const params = {};
      if (filterValue) params.filterValue = filterValue;
      const { data } = await client.get(
        '/marketplace/dashboard/type/' + metricType,
        { params },
      );
      marketplaceMetrics.value = data;
      return data;
    } catch (err) {
      marketplaceError.value = err.response?.data?.message || err.message;
      throw err;
    } finally {
      marketplaceLoading.value = false;
    }
  }

  async function fetchMarketplaceForDate(date) {
    marketplaceLoading.value = true;
    marketplaceError.value = null;
    try {
      const { data } = await client.get('/marketplace/dashboard/date/' + date);
      marketplaceMetrics.value = data;
      return data;
    } catch (err) {
      marketplaceError.value = err.response?.data?.message || err.message;
      throw err;
    } finally {
      marketplaceLoading.value = false;
    }
  }

  async function refreshMarketplaceDaily() {
    try {
      await client.post('/marketplace/dashboard/refresh');
      return fetchMarketplaceCurrent();
    } catch (err) {
      marketplaceError.value = err.response?.data?.message || err.message;
      throw err;
    }
  }

  // ── Group Dashboard Fetchers ────────────────────────────────────────────────
  async function fetchGroupCurrent(groupId) {
    if (!groupId) return null;
    groupLoading.value = true;
    groupError.value = null;
    try {
      const { data } = await client.get(
        '/groups/' + groupId + '/dashboard/current',
      );
      groupMetrics.value = data;
      return data;
    } catch (err) {
      groupError.value = err.response?.data?.message || err.message;
      throw err;
    } finally {
      groupLoading.value = false;
    }
  }

  async function fetchGroupForDate(groupId, date) {
    if (!groupId) return null;
    groupLoading.value = true;
    groupError.value = null;
    try {
      const { data } = await client.get(
        '/groups/' + groupId + '/dashboard/date/' + date,
      );
      groupMetrics.value = data;
      return data;
    } catch (err) {
      groupError.value = err.response?.data?.message || err.message;
      throw err;
    } finally {
      groupLoading.value = false;
    }
  }

  async function fetchGroupHistorical(groupId, startDate, endDate) {
    if (!groupId) return [];
    groupLoading.value = true;
    groupError.value = null;
    try {
      const { data } = await client.get(
        '/groups/' + groupId + '/dashboard/historical',
        { params: { startDate, endDate } },
      );
      return data;
    } catch (err) {
      groupError.value = err.response?.data?.message || err.message;
      throw err;
    } finally {
      groupLoading.value = false;
    }
  }

  async function refreshGroupDaily(groupId) {
    if (!groupId) return null;
    try {
      await client.post('/groups/' + groupId + '/dashboard/refresh');
      return fetchGroupCurrent(groupId);
    } catch (err) {
      groupError.value = err.response?.data?.message || err.message;
      throw err;
    }
  }

  function clearAdmin() {
    adminMetrics.value = null;
    adminError.value = null;
  }

  function clearMarketplace() {
    marketplaceMetrics.value = null;
    marketplaceError.value = null;
    selectedMetricType.value = 'GLOBAL';
    marketplaceFilterValue.value = '';
  }

  function clearGroup() {
    groupMetrics.value = null;
    groupError.value = null;
  }

  return {
    // State
    adminMetrics,
    marketplaceMetrics,
    groupMetrics,
    selectedMetricType,
    marketplaceFilterValue,
    adminLoading,
    marketplaceLoading,
    groupLoading,
    adminError,
    marketplaceError,
    groupError,

    // Admin
    fetchAdminCurrent,
    fetchAdminForDate,
    fetchAdminHistorical,
    refreshAdminDaily,

    // Marketplace
    fetchMarketplaceCurrent,
    fetchMarketplaceByType,
    fetchMarketplaceForDate,
    refreshMarketplaceDaily,

    // Group
    fetchGroupCurrent,
    fetchGroupForDate,
    fetchGroupHistorical,
    refreshGroupDaily,

    // Clear helpers
    clearAdmin,
    clearMarketplace,
    clearGroup,
  };
}